import React, { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  Stack,
  Typography,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginUser, sendLoginOtp, setAuthToken, verifyLoginOtp } from '../services/api';
import { clearOrders, clearAllRiderOfflineData } from '../utils/indexedDB';
import { isPhoneIdentifier } from '../utils/loginUtils';
import { getDefaultAppPath } from '../utils/whatsNew';
import { syncRiderServiceWorker } from '../utils/riderOffline';
import { scheduleSessionExpiryLogout } from '../utils/sessionAuth';
import AuthLayout from './layout/AuthLayout';
import { neonBlue, stormGrey } from '../theme/colors';

const completeLogin = async (response, navigate) => {
  if (!response.data || !response.data.token) {
    throw new Error('Invalid response from server');
  }

  const userRole = response.data.user.role || 'clerk';
  const previousUserId = localStorage.getItem('userId');
  const nextUserId = String(response.data.user.id);

  // Clear offline rider data when switching accounts on a shared device
  if (previousUserId && previousUserId !== nextUserId) {
    try {
      await clearAllRiderOfflineData();
    } catch (clearError) {
      // ignore
    }
  } else if (userRole !== 'rider') {
    try {
      await clearOrders();
    } catch (clearError) {
      // ignore
    }
  }

  setAuthToken(response.data.token);
  localStorage.setItem('token', response.data.token);
  localStorage.setItem('userRole', userRole);
  localStorage.setItem('userId', nextUserId);
  localStorage.setItem('username', response.data.user.username);
  localStorage.setItem('companyId', response.data.user.company_id || '');
  localStorage.setItem('companyName', response.data.user.Company?.name || 'Default Company');

  syncRiderServiceWorker(userRole);
  scheduleSessionExpiryLogout();
  navigate(getDefaultAppPath(userRole), { replace: true });
};

const getLoginErrorMessage = (err) => {
  const errorMessage = err.response?.data?.message;
  if (errorMessage?.includes('Company account is inactive')) {
    return 'Your company account is inactive. Please contact the administrator.';
  }
  if (errorMessage?.includes('Account is inactive')) {
    return 'Your account is inactive. Please contact the administrator.';
  }
  return errorMessage || 'Login failed. Please check your credentials.';
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    borderRadius: 1.5,
    '& fieldset': {
      borderColor: stormGrey[300],
    },
    '&:hover fieldset': {
      borderColor: stormGrey[400],
    },
    '&.Mui-focused fieldset': {
      borderColor: neonBlue[500],
    },
  },
};

const pillButtonSx = {
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 600,
  py: 1.35,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
};

const Login = () => {
  const [loginTab, setLoginTab] = useState(0);
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('reason') === 'session_expired';

  const isOtpTab = loginTab === 1;
  const isValidPhone = useMemo(() => isPhoneIdentifier(phone), [phone]);
  const canPasswordSignIn = Boolean(username.trim() && password);
  const canOtpSignIn = Boolean(isValidPhone && otpSent && otp.trim().length >= 4);

  const handleTabChange = (_event, nextTab) => {
    setLoginTab(nextTab);
    setError('');
    setInfo('');
    setOtp('');
    setOtpSent(false);
  };

  const handleSendOtp = async () => {
    setError('');
    setInfo('');
    setIsSendingOtp(true);

    try {
      const response = await sendLoginOtp(phone.trim());
      setOtpSent(true);
      setInfo(response.data?.message || 'OTP sent successfully');
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (isOtpTab) {
      if (!canOtpSignIn) {
        return;
      }
    } else if (!canPasswordSignIn) {
      return;
    }

    setIsLoading(true);

    try {
      const response = isOtpTab
        ? await verifyLoginOtp(phone.trim(), otp.trim())
        : await loginUser(username.trim(), password);

      await completeLogin(response, navigate);
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title="Sign In To Your Account">
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={loginTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 44,
            borderBottom: `1px solid ${stormGrey[200]}`,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: stormGrey[500],
              minHeight: 44,
            },
            '& .Mui-selected': {
              color: `${neonBlue[600]} !important`,
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              bgcolor: neonBlue[600],
            },
          }}
        >
          <Tab label="Password Sign In" />
          <Tab label="OTP Sign In" />
        </Tabs>
      </Box>

      {sessionExpired && !error && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          Your session expired after 10 minutes of inactivity. Please sign in again.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      {info && (
        <Alert severity="success" sx={{ mb: 2.5 }}>
          {info}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          {!isOtpTab ? (
            <>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: stormGrey[900] }}>
                  Username
                </Typography>
                <TextField
                  required
                  fullWidth
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError('');
                  }}
                  autoFocus
                  autoComplete="username"
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: stormGrey[900] }}>
                  Password
                </Typography>
                <TextField
                  required
                  fullWidth
                  placeholder="Enter your password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  autoComplete="current-password"
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Typography variant="body2" sx={{ color: stormGrey[500] }}>
                    Forgot password? Contact your administrator.
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: stormGrey[900] }}>
                  Phone Number
                </Typography>
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <TextField
                    required
                    fullWidth
                    placeholder="254xxxxxxxxx"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setOtpSent(false);
                      setOtp('');
                      setError('');
                      setInfo('');
                    }}
                    autoFocus
                    autoComplete="tel"
                    sx={fieldSx}
                  />
                  <Button
                    type="button"
                    variant="contained"
                    disabled={!isValidPhone || isSendingOtp || isLoading}
                    onClick={handleSendOtp}
                    sx={{
                      ...pillButtonSx,
                      px: 2.5,
                      minWidth: 110,
                      whiteSpace: 'nowrap',
                      bgcolor: isValidPhone ? neonBlue[600] : stormGrey[200],
                      color: isValidPhone ? '#fff' : stormGrey[500],
                      '&:hover': {
                        bgcolor: isValidPhone ? neonBlue[700] : stormGrey[200],
                        boxShadow: 'none',
                      },
                      '&.Mui-disabled': {
                        bgcolor: stormGrey[200],
                        color: stormGrey[500],
                      },
                    }}
                  >
                    {isSendingOtp ? 'Sending...' : otpSent ? 'Resend' : 'Get Code'}
                  </Button>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: stormGrey[500] }}>
                  Use a valid Kenyan phone number (254xxxxxxxxx).
                </Typography>
              </Box>

              {otpSent && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: stormGrey[900] }}>
                    OTP Code
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    placeholder="Enter the 6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                    autoComplete="one-time-code"
                    sx={fieldSx}
                  />
                </Box>
              )}
            </>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={
              isLoading
              || isSendingOtp
              || (isOtpTab ? !canOtpSignIn : !canPasswordSignIn)
            }
            sx={{
              ...pillButtonSx,
              mt: 1,
              bgcolor: neonBlue[600],
              '&:hover': {
                bgcolor: neonBlue[700],
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                bgcolor: stormGrey[200],
                color: stormGrey[500],
              },
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default Login;
